
import { Injectable, signal, computed, inject, OnDestroy } from '@angular/core';
import { DataService } from './data.service';
import { AuthService } from './auth.service';
// FIX: Corrected import path for models.
import { Customer, Ticket, AppUser } from '../models/index';
import { getSupabase } from '../firebase';
import { SupabaseClient } from '@supabase/supabase-js';

@Injectable({
  providedIn: 'root',
})
export class PosStateService implements OnDestroy {
  private dataService = inject(DataService);
  private authService = inject(AuthService);
  private supabase: SupabaseClient | null = getSupabase();

  // === RAW STATE SIGNALS ===
  customers = signal<Customer[]>([]);
  
  // In Intake mode, we still use a "ticket" concept to hold the current session state (selected customer)
  // even though we don't put products in it anymore.
  tickets = signal<Ticket[]>([{ id: 1, items: [], customerId: null, discountType: null, discountValue: 0, prescriptionId: null }]);
  activeTicketId = signal<number>(1);
  
  // New Signal for Intake/Symptom Data
  activeIntakeSymptoms = signal<string>('');

  // Error state signals
  customersError = signal<string | null>(null);
  
  // QR Payment State
  qrPaymentState = signal<'idle' | 'generating' | 'waiting' | 'success' | 'failed'>('idle');
  qrCodeData = signal<string | null>(null);
  qrPaymentError = signal<string | null>(null);
  
  // === COMPUTED / DERIVED STATE ===
  activeTicket = computed(() => this.tickets().find(t => t.id === this.activeTicketId()));
  activeCustomer = computed(() => this.customers().find(c => c.id === this.activeTicket()?.customerId));

  constructor() {}

  ngOnDestroy() {}

  // === PUBLIC API METHODS ===

  isSupabaseEnabled(): boolean {
    return this.dataService.isSupabaseEnabled();
  }

  async initialize(): Promise<void> {
    this.customersError.set(null);
    await this.reloadCustomers();
  }

  async reloadCustomers(): Promise<void> {
    this.customersError.set(null);
    try {
      this.customers.set(await this.dataService.getCustomers());
    } catch (err) {
      const message = (err as Error).message;
      console.error('Failed to load customers:', message);
      this.customers.set([]);
      this.customersError.set(message);
    }
  }

  clearActiveTicket(): void {
    this.activeIntakeSymptoms.set('');
    this.qrPaymentState.set('idle'); // Reset QR state
    this.qrCodeData.set(null);
    this.tickets.update(tickets => {
      const index = tickets.findIndex(t => t.id === this.activeTicketId());
      if (index === -1) return tickets;
      const newTickets = [...tickets];
      // Clear customer association
      newTickets[index] = { ...newTickets[index], customerId: null, prescriptionId: null };
      return newTickets;
    });
  }
  
  async assignCustomerToTicket(customer: Customer): Promise<{ success: boolean, message?: string }> {
    if (this._isCustomerUnderage(customer.birthDate)) {
      return { success: false, message: `กฎหมายห้ามจำหน่ายสินค้าให้กับลูกค้าที่อายุต่ำกว่า 20 ปี` };
    }
    this.tickets.update(tickets => {
      const index = tickets.findIndex(t => t.id === this.activeTicketId());
      if (index === -1) return tickets;
      const newTickets = [...tickets];
      newTickets[index] = { ...newTickets[index], customerId: customer.id };
      return newTickets;
    });
    return { success: true };
  }

  // --- INTAKE SUBMISSION (The Core Function) ---
  async submitIntake(symptoms: string): Promise<void> {
    const customer = this.activeCustomer();
    const user = this.authService.currentUser();
    
    if (!customer) throw new Error('Please select a patient first.');
    if (!user?.storeId || !user?.uid) throw new Error('Error: Staff or store information is missing.');
    if (!symptoms.trim()) throw new Error('Please enter initial symptoms or reason for visit.');

    // Create PENDING prescription with symptoms
    await this.dataService.createPrescription(
        customer.id, 
        user.storeId, 
        user.uid, 
        [symptoms] // Pass symptoms array
    );

    // Clear the session
    this.clearActiveTicket();
  }

  // --- PAYMENT LOGIC (QR Code Generation) ---
  generateQrPayment(amount: number): void {
    this.qrPaymentState.set('generating');
    this.qrPaymentError.set(null);
    this.qrCodeData.set(null);

    // 1. Get Store PromptPay ID
    const store = this.authService.currentStore();
    // Check fields flexibly as promptpay_number might be in snake_case in some DB versions
    const promptPayId = (store as any)?.promptpay_number || (store as any)?.promptpayNumber;

    if (!promptPayId) {
        this.qrPaymentState.set('failed');
        this.qrPaymentError.set('Store PromptPay ID not configured. Please go to Settings > General.');
        return;
    }

    // 2. Generate Payload (Using promptpay.io for demo simplicity, or GeneratePayload library in production)
    setTimeout(() => {
        try {
            const qrUrl = `https://promptpay.io/${promptPayId}/${amount}.png`;
            this.qrCodeData.set(qrUrl);
            this.qrPaymentState.set('waiting');
        } catch (e) {
            this.qrPaymentState.set('failed');
            this.qrPaymentError.set('Failed to generate QR Code.');
        }
    }, 800); // Simulate API delay
  }

  confirmQrPayment(): void {
      this.qrPaymentState.set('success');
  }

  cancelQrPayment(): void {
      this.qrPaymentState.set('idle');
      this.qrCodeData.set(null);
      this.qrPaymentError.set(null);
  }

  private _isCustomerUnderage(birthDateString?: string): boolean {
    if (!birthDateString) return false;
    const birthDate = new Date(birthDateString);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return age < 20;
  }
}
